import { createContext, useContext, useEffect, useState } from "react";

import { BrandedLoader } from "@/components/layout/BrandedLoader";

/**
 * Vrai à l'intérieur de la mise en page de l'application (barre du haut, pied
 * de page). Fourni par `ProtectedLayout`.
 */
export const InAppShellContext = createContext(false);

/** Délai avant d'afficher le squelette : une page déjà en cache arrive avant,
 *  et rien ne doit clignoter. */
const SKELETON_DELAY_MS = 150;

/**
 * Attente d'une page chargée à la demande (Suspense de chaque route).
 *
 * - Hors de l'application (première ouverture, connexion, pages publiques) :
 *   l'écran au logo, qui prolonge le splash d'`index.html` sans saut visuel.
 * - Dans l'application (changement de page) : un squelette discret à la place
 *   du contenu ; la barre du haut et la navigation restent en place.
 */
export function PageFallback() {
  const inShell = useContext(InAppShellContext);
  return inShell ? <PageSkeleton /> : <BrandedLoader />;
}

function PageSkeleton() {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setVisible(true), SKELETON_DELAY_MS);
    return () => clearTimeout(t);
  }, []);

  return (
    <div role="status" aria-live="polite" className="space-y-4 p-4 sm:p-6">
      <span className="sr-only">Chargement de la page…</span>
      {visible && (
        <div className="animate-pulse space-y-4 motion-reduce:animate-none" aria-hidden>
          <div className="h-3 w-40 bg-border" />
          <div className="h-7 w-64 max-w-full bg-border" />
          <div className="space-y-2 border border-border bg-surface p-4">
            {Array.from({ length: 6 }, (_, i) => (
              <div key={i} className="h-4 bg-border/70" style={{ width: `${90 - i * 7}%` }} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
